import { CalendarOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Modal, Popconfirm, Row, Select, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { selectAllDeliveryAreas } from '../../../modules/delivery-areas/stores/slice';
import {
  doCreateDeliveryAreas,
  doDeleteDeliveryAreas,
  doGetAllDeliveryAreas,
  doUpdateDeliveryArea,
} from '../../../modules/delivery-areas/stores/thunk';
import { selectAllLocations } from '../../../modules/locations/stores/slice';
import { doGetLocations } from '../../../modules/locations/stores/thunk';
import DeliveryAreaForm from '../../Forms/Settings/DeliveryAreaForm';

import { OffSchedule } from '../../Inputs/OffSchedule';
import { doGetCarriers } from '../../../modules/carriers/stores/thunk';
import { selectAllCarriers } from '../../../modules/carriers/stores/slice';
const { Meta } = Card;

const DeliveryArea = () => {
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [areaDetail, setAreaDetail] = useState(undefined);
  const [isNewArea, setIsNewArea] = useState(false);
  const [selectedAreaType, setSelectedAreaType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const isFetchingDeliveryAreas = useSelector((state) => state.deliveryAreas.isFetching);
  const action = useSelector((state) => state.deliveryAreas.action);
  const status = useSelector((state) => state.deliveryAreas.status);
  const deliveryAreas = useSelector((state) => selectAllDeliveryAreas(state));
  const locations = useSelector((state) => selectAllLocations(state));
  const carriers = useSelector((state) => selectAllCarriers(state));

  useEffect(() => {
    if (selectedLocation) {
      dispatch(doGetAllDeliveryAreas({ locationId: selectedLocation }));
    } else {
      dispatch(doGetAllDeliveryAreas());
    }
    dispatch(doGetLocations({ page: 1, take: 100000 }));
  }, [selectedLocation]);

  useEffect(() => {
    if (['create', 'update', 'delete'].includes(action) && status === 'succeeded') {
      if (selectedLocation) {
        dispatch(doGetAllDeliveryAreas({ locationId: selectedLocation }));
      } else {
        dispatch(doGetAllDeliveryAreas());
      }

      dispatch(doGetLocations({ page: 1, take: 100000 }));
      cancelModal();
    }
  }, [isFetchingDeliveryAreas]);

  useEffect(() => {
    dispatch(doGetCarriers());
  }, []);

  const onClickNewArea = () => {
    setIsNewArea(true);
    setIsModalOpen(true);
  };

  const onClickDeleteArea = (id) => {
    dispatch(doDeleteDeliveryAreas({ id }));
  };

  const onClickEditArea = (area) => {
    setAreaDetail({
      ...area,
    });
    setIsNewArea(false);
    setIsModalOpen(true);
  };

  const handleActionArea = (data) => {
    if (data.area) {
      data.area = Object.values(data.area);
    }
    if (data.id) {
      dispatch(doUpdateDeliveryArea({ ...data }));
    } else {
      dispatch(doCreateDeliveryAreas({ ...data }));
    }
  };

  const cancelModal = () => {
    if (areaDetail) setAreaDetail(undefined);
    setIsModalOpen(false);
  };

  const descriptionTag = (type) => {
    if (type === 'AREA') {
      return <>Polygon</>;
    } else if (type === 'POSTAL_CODES') {
      return <>Postal codes</>;
    }
  };

  const [selectedDeliveryArea, setSelectedDeliveryArea] = useState(null);
  const [offScheduleOpen, setOffScheduleOpen] = useState(false);

  const onClickEditOffSchedule = (id) => {
    const deliveryArea = deliveryAreas.find((l) => l.id === id);
    setSelectedDeliveryArea({
      ...deliveryArea,
    });

    setOffScheduleOpen(true);
  };

  return (
    <Spin spinning={isFetchingDeliveryAreas}>
      <div className="info">
        <div className="info-content">
          <h3>Delivery Areas</h3>
          <h5>Find and create new delivery areas</h5>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <div style={{ display: 'flex', gap: '12px' }}>
            <Select
              size="small"
              className="select-small"
              style={{ width: '150px' }}
              defaultValue={''}
              onChange={(value) => setSelectedAreaType(value)}
              options={[
                { label: 'All types', value: '' },
                { label: 'Polygons', value: 'AREA' },
                { label: 'Postal Codes', value: 'POSTAL_CODES' },
              ]}
            />
            <Select
              size="small"
              defaultValue={''}
              className="select-small"
              style={{ width: '250px' }}
              onChange={(value) => setSelectedLocation(value)}
              options={[
                { label: 'All locations', value: '' },
                ...(locations?.map((location) => ({
                  label: location.name,
                  value: location.id,
                })) || []), // Empty array as fallback
              ]}
            />
          </div>

          <Button className="CreateButton ButtonWhite" size="small" onClick={onClickNewArea}>
            Add Delivery Area
            <ReactSVG className="icon" src="/icons/plus.svg" />
          </Button>
        </div>
        <Row gutter={[16, 16]}>
          {deliveryAreas
            ?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .filter((area) => selectedAreaType === '' || area.type === selectedAreaType)
            .map((area) => (
              <Col key={area.id} span={8}>
                <Card
                  style={{ height: '100%' }}
                  actions={[
                    <>
                      <CalendarOutlined onClick={() => onClickEditOffSchedule(area.id)} />
                    </>,
                    <>
                      <EditOutlined onClick={() => onClickEditArea(area)} key={`edit-${area.id}`} />
                    </>,
                    <>
                      <Popconfirm
                        title="Are you sure you want to delete this delivery area?"
                        onConfirm={() => onClickDeleteArea(area.id)}
                        okText="Yes"
                        cancelText="No"
                        key={`pop-${area.id}`}
                      >
                        <DeleteOutlined key={`delete-${area.id}`} />
                      </Popconfirm>
                    </>,
                  ]}
                >
                  <Meta
                    title={area.name}
                    description={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <p style={{ textAlign: 'left', color: 'gray', fontSize: '14px' }}>
                          {descriptionTag(area.type)}
                        </p>
                        {/* <p style={{ textAlign: 'right' }}>
                          <Tooltip title={accountLookup[area.accountId]?.name}>
                            <img
                              src={
                                accountLookup[area.accountId]?.logoThumbUrl ||
                                `https://ui-avatars.com/api/?length=1&name=${
                                  accountLookup[area.accountId]?.name
                                }&size=32&font-size=0.4&bold=true&background=${COLOR_DEFAULT}&color=${SPARQLE_DARK_BLUE}&rounded=true`
                              }
                              style={{ borderRadius: '50%' }}
                            />
                          </Tooltip>
                        </p> */}
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
        </Row>
        <Modal
          width={1104}
          closeIcon={false}
          title={isNewArea ? 'Add Delivery Area' : 'Edit Delivery Area'}
          open={isModalOpen}
        >
          <DeliveryAreaForm
            isModalOpen={isModalOpen}
            handleCancel={cancelModal}
            isNewArea={isNewArea}
            area={areaDetail}
            handleOk={(values) => handleActionArea(values)}
            carriers={carriers}
          />
        </Modal>
        <OffSchedule
          open={offScheduleOpen}
          close={() => setOffScheduleOpen(false)}
          deliveryArea={selectedDeliveryArea}
        />
      </div>
    </Spin>
  );
};

export default DeliveryArea;
